import { ButtonVanilla, Img, Typography } from '@ag1/ui';
import { sendAddToCartEvent } from '@portal/analytics/events/ecommerce';
import { CollectionProduct } from '@portal/api/shopifyV2/types';
import { PRODUCTS_BY_STORE } from '@portal/shared/components/CartModal/constants';
import { useCartModalCarouselProductsV2 } from '@portal/shared/components/CartModal/hooks/useCartModalCarouselProductsV2';
import { useSiteCart } from '@portal/shared/components/SiteCart/hooks/SiteCartContext';
import { useMoney } from '@portal/shared/hooks/useMoney';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AG1_KEY, AGZ_KEY, CADENCE_OPTIONS, isKeyInTitle } from '../../constants';
import styles from './SiteCartUpsell.module.scss';
import { useSessionValue } from '@portal/appLayer/auth';
import { useStoreCurrency } from '@portal/state/hooks/useStoreCurrency';

type UpsellItemProps = {
  item: CollectionProduct;
  isLoading: boolean;
  handleAddUpsell: () => void;
  isAddBlocked?: boolean;
};
const UpsellItem = ({ item, isLoading, handleAddUpsell, isAddBlocked }: UpsellItemProps) => {
  const { formatShopifyMoney } = useMoney();
  const image = item.images[0];
  const firstVariant = item.variants[0];

  return (
    <div className={styles.upsellItem}>
      <Img src={image?.src} alt={image?.altText || ''} className={styles.upsellImage} />
      <div className={styles.upsellDetails}>
        <Typography variant="body" size="2" weight="700" noPadding>
          {item.title}
        </Typography>
        <div className={styles.upsellSubDetails}>
          <Typography variant="caption" size="1" noPadding className={styles.upsellPrice}>
            {formatShopifyMoney(firstVariant.price, { hideDecimalsForWholeNumbers: true })}
          </Typography>
          <ButtonVanilla
            shape="round"
            variant="tertiary"
            size="XS"
            onClick={handleAddUpsell}
            disabled={isLoading || !!isAddBlocked}
            aria-disabled={isLoading || !!isAddBlocked}
            title={
              isAddBlocked
                ? 'Add at least one paid item to your cart to claim this free item'
                : undefined
            }>
            Add
          </ButtonVanilla>
        </div>
      </div>
    </div>
  );
};

export const SiteCartUpsell = ({ showTitle = false }: { showTitle?: boolean }) => {
  const { cartId, otpItems, subItems, addLinesToCart, isCartUpdating } = useSiteCart();
  const { t } = useTranslation('siteCart');
  const store = useStoreValue();
  const router = useRouter();
  const session = useSessionValue();
  const { currency } = useStoreCurrency();
  const pageSlug = router.asPath;
  const utmCampaign = router.query.utm_campaign;

  const isCartEmpty = !otpItems || otpItems.length === 0;
  const isFreeVariant = (p: CollectionProduct) => {
    const v = p?.variants?.[0];
    // handle MoneyV2 shape (string amount) or number fallback
    const amt = typeof v?.price?.amount !== 'undefined' ? v.price.amount : v?.price;
    const n = typeof amt === 'string' ? parseFloat(amt) : Number(amt);
    return Number.isFinite(n) && n === 0;
  };

  const showScoopFirstInCarousel = useMemo(
    () =>
      otpItems.some((line) =>
        [
          PRODUCTS_BY_STORE[store].subPouch,
          PRODUCTS_BY_STORE[store].subPouchV53,
          PRODUCTS_BY_STORE[store].subPouchV53Tropical
        ].includes(line.merchandise.id)
      ),
    [otpItems, store]
  );

  const { products } = useCartModalCarouselProductsV2({
    showScoopFirstInCarousel
  });

  const handleAddUpsell = (item: CollectionProduct, index: number) => {
    // Block adding free items to cart when cart is empty
    if (isCartEmpty && isFreeVariant(item)) {
      return;
    }
    const id = item.variants?.[0]?.id;
    if (!id) return;

    // Select selling plan from existing cart plan or default
    const existingCartSellingPlanId = subItems.find(
      (item) => item.sellingPlanAllocation?.sellingPlan.id
    )?.sellingPlanAllocation?.sellingPlan.id;
    const monthlyDefaultCadenceOption = CADENCE_OPTIONS[store]?.find((c) => c.label.includes('30'))
      ?.value;
    const chosenSellingPlanId = existingCartSellingPlanId || monthlyDefaultCadenceOption;
    // Only add selling plans for AG1 and AGZ core products
    const shouldAddSellingPlan =
      isKeyInTitle(AG1_KEY, item.title) || isKeyInTitle(AGZ_KEY, item.title);

    addLinesToCart([
      {
        merchandiseId: id,
        quantity: 1,
        ...(shouldAddSellingPlan && !!chosenSellingPlanId
          ? {
              sellingPlanId: chosenSellingPlanId,
              attributes: [
                {
                  key: '__shop_subscriptions',
                  value: 'true'
                }
              ]
            }
          : {})
      }
    ]);
    sendAddToCartEvent({
      eventId: cartId,
      checkoutType: 'unified',
      cartStatus: 'Full',
      store,
      ecommerce: {
        item_list_name: 'Cart Carousel',
        item_list_id: 'Cart Carousel',
        affiliation: typeof utmCampaign === 'string' ? utmCampaign : pageSlug,
        items: [
          {
            item_name: item.title,
            item_id: item.id,
            item_variant: item.variants[0].title,
            item_checkout_type: 'unified',
            item_category: 'OTP',
            variant_id: item.variants[0].id,
            price: item.variants[0].price.amount,
            quantity: 1,
            index: index + 1
          }
        ],
        currency,
        value: item.variants[0].price.amount
      },
      user: {
        email: session?.customer.email || ''
      }
    });
  };

  return (
    <div className={styles.container}>
      {showTitle && (
        <div className={styles.titleContainer}>
          <Typography variant="captionMono" size="1" className={styles.sectionTitle}>
            {t('upsellTitle')}
          </Typography>
        </div>
      )}
      {/* Upsell Carousel */}
      <div className={styles.upsellCarousel}>
        {products.map((item, index) => {
          const blocked = isCartEmpty && isFreeVariant(item);
          return (
            <UpsellItem
              key={item.id}
              item={item}
              handleAddUpsell={() => handleAddUpsell(item, index)}
              isLoading={isCartUpdating}
              isAddBlocked={blocked}
            />
          );
        })}
      </div>
    </div>
  );
};
